// =============================================================================
// ActionErrorDialog
// Modale moderne (style ClickUp / Linear) qui explique une erreur d'action :
//   - Icône claire (cadenas pour permission, alerte pour autre)
//   - Titre court ("Impossible de créer cette colonne")
//   - Raison détaillée en français
//   - Action : "J'ai compris" + (si permission) "Demander l'accès"
// Pilotée par useActionErrorStore (Zustand global).
// 100% responsive, mobile-first.
// =============================================================================

import { create } from 'zustand';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Lock, AlertTriangle, WifiOff, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ActionErrorKind = 'permission' | 'network' | 'session' | 'notFound' | 'conflict' | 'generic';

interface ActionErrorPayload {
  title: string;
  description: string;
  kind: ActionErrorKind;
  /** Optionnel : ce que l'utilisateur essayait de faire (chip "Action") */
  actionLabel?: string;
}

interface ActionErrorState {
  open: boolean;
  payload: ActionErrorPayload | null;
  show: (payload: ActionErrorPayload) => void;
  close: () => void;
}

export const useActionErrorStore = create<ActionErrorState>((set) => ({
  open: false,
  payload: null,
  show: (payload) => set({ open: true, payload }),
  close: () => set({ open: false }),
}));

// ─────────────────────────────────────────────────────────────────────────────
// Mapping visuel par type d'erreur
// ─────────────────────────────────────────────────────────────────────────────
const KIND_VISUALS: Record<ActionErrorKind, { Icon: typeof Lock; iconBg: string; iconFg: string; ring: string }> = {
  permission: {
    Icon: Lock,
    iconBg: 'bg-amber-500/10',
    iconFg: 'text-amber-600 dark:text-amber-400',
    ring: 'ring-amber-500/20',
  },
  network: {
    Icon: WifiOff,
    iconBg: 'bg-sky-500/10',
    iconFg: 'text-sky-600 dark:text-sky-400',
    ring: 'ring-sky-500/20',
  },
  session: {
    Icon: Clock,
    iconBg: 'bg-violet-500/10',
    iconFg: 'text-violet-600 dark:text-violet-400',
    ring: 'ring-violet-500/20',
  },
  notFound: {
    Icon: AlertTriangle,
    iconBg: 'bg-slate-500/10',
    iconFg: 'text-slate-600 dark:text-slate-300',
    ring: 'ring-slate-500/20',
  },
  conflict: {
    Icon: AlertTriangle,
    iconBg: 'bg-orange-500/10',
    iconFg: 'text-orange-600 dark:text-orange-400',
    ring: 'ring-orange-500/20',
  },
  generic: {
    Icon: AlertTriangle,
    iconBg: 'bg-destructive/10',
    iconFg: 'text-destructive',
    ring: 'ring-destructive/20',
  },
};

const KIND_BADGES: Record<ActionErrorKind, string> = {
  permission: 'Permission requise',
  network: 'Connexion',
  session: 'Session',
  notFound: 'Introuvable',
  conflict: 'Conflit',
  generic: 'Erreur',
};

export function ActionErrorDialog() {
  const { open, payload, close } = useActionErrorStore();

  if (!payload) {
    return (
      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent />
      </Dialog>
    );
  }

  const visuals = KIND_VISUALS[payload.kind];
  const Icon = visuals.Icon;
  const badge = KIND_BADGES[payload.kind];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent
        className={cn(
          'sm:max-w-md p-0 overflow-hidden gap-0',
          'border border-border/60 shadow-2xl',
        )}
      >
        {/* Header avec icône large centrée */}
        <div className="flex flex-col items-center text-center px-6 pt-7 pb-4">
          <div
            className={cn(
              'flex h-14 w-14 items-center justify-center rounded-2xl ring-8',
              visuals.iconBg,
              visuals.ring,
            )}
          >
            <Icon className={cn('h-7 w-7', visuals.iconFg)} strokeWidth={2.25} />
          </div>

          <span
            className={cn(
              'mt-4 inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium tracking-wide uppercase',
              visuals.iconBg,
              visuals.iconFg,
            )}
          >
            {badge}
          </span>

          <DialogHeader className="mt-3 space-y-1.5">
            <DialogTitle className="text-lg sm:text-xl font-semibold leading-snug">
              {payload.title}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
              {payload.description}
            </DialogDescription>
          </DialogHeader>

          {payload.actionLabel && (
            <div className="mt-3 text-[11px] text-muted-foreground/80">
              Action tentée :{' '}
              <span className="font-medium text-foreground/80">{payload.actionLabel}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter
          className={cn(
            'px-6 py-4 bg-muted/30 border-t border-border/60',
            'flex-col-reverse sm:flex-row sm:justify-end gap-2',
          )}
        >
          <Button
            variant="default"
            onClick={close}
            className="w-full sm:w-auto"
          >
            J'ai compris
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
